import React, { useState, useEffect } from 'react';
import { useOnboardingGuide } from '../../hooks/useOnboardingGuide';
import { useLocation } from 'react-router';

interface NextStepIndicatorProps {
  className?: string;
}

const NextStepIndicator: React.FC<NextStepIndicatorProps> = ({ className = '' }) => {
  const {
    nextStep,
    progress,
    showGuidance,
    goToNextStep,
    toggleGuidance,
    isComplete
  } = useOnboardingGuide();

  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Hide indicator on certain pages
  const shouldHide = [
    '/onboarding'
  ].some(path => location.pathname.startsWith(path));

  useEffect(() => {
    // Show indicator if we have guidance enabled, next step available, and not on excluded pages
    const shouldShow = showGuidance && nextStep && !shouldHide && !isComplete;
    setIsVisible(shouldShow);
  }, [showGuidance, nextStep, shouldHide, isComplete]);

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleContinue = () => {
    setIsExpanded(false);
    goToNextStep();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    toggleGuidance(false);
  };

  // TEMPORAL: Ocultar el botón flotante de onboarding
  return null;
  
  if (!isVisible || !nextStep) {
    return null;
  }

  return (
    <div className={`fixed bottom-6 left-6 z-40 ${className}`}>
      <div className="relative">
        {/* Main indicator button */}
        <button
          onClick={handleToggleExpanded}
          className={`relative group transition-all duration-300 ${
            isExpanded 
              ? 'w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-gray-700' 
              : 'w-14 h-14 bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg hover:shadow-xl'
          }`}
        >
          {!isExpanded ? (
            <>
              {/* Pulse animation for attention */}
              <div className="absolute -inset-1 bg-blue-400 rounded-full animate-ping opacity-30" />
              
              {/* Icon and progress indicator */}
              <div className="relative w-full h-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                
                {/* Progress ring */}
                <div className="absolute inset-0">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 56 56">
                    <circle
                      cx="28"
                      cy="28"
                      r="26"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-blue-200"
                      opacity="0.3"
                    />
                    <circle
                      cx="28"
                      cy="28"
                      r="26"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-white"
                      strokeDasharray={`${2 * Math.PI * 26}`}
                      strokeDashoffset={`${2 * Math.PI * 26 * (1 - progress / 100)}`}
                      style={{
                        transition: 'stroke-dashoffset 0.5s ease-in-out',
                      }}
                    />
                  </svg>
                </div>
              </div>
            </>
          ) : (
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Siguiente paso
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {progress}% completado
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleToggleExpanded}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Next step info */}
              <div className="mb-4">
                <div className="flex items-start space-x-3 p-3 bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-950/20 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400">
                    {nextStep.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {nextStep.title}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {nextStep.description}
                    </p>
                    {nextStep.optional && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 mt-2">
                        Opcional
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Progreso
                  </span>
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-blue-700 h-1.5 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={handleContinue}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                >
                  <span>Continuar</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-3 py-2 text-gray-600 dark:text-gray-400 text-sm hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  title="Ocultar guía"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </button>

        {/* Tooltip for collapsed state */}
        {!isExpanded && (
          <div className="absolute left-16 bottom-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
              <div className="flex items-center space-x-2">
                <span>Siguiente: {nextStep.title}</span>
                <div className="text-gray-300">
                  ({progress}%)
                </div>
              </div>
              {/* Arrow */}
              <div className="absolute right-full top-1/2 -translate-y-1/2">
                <div className="border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NextStepIndicator;